import { LearnerPageChrome } from "@/pages/learners/page-chrome"
import {
  learnersStudentChangeWishlist,
  learnersStudentChangeWishlistCancellation,
  learnersStudentChangeWishlistPublication,
} from "@/routes"
import { ActionButton } from "@/components/shared/action-buttons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PageBody } from "@/components/ui/layout"
import { SheetPageBody, SheetPageTitle } from "@/components/ui/sheet-page"
import {
  AlertCircle,
  Ban,
  ChevronDown,
  ChevronUp,
  CirclePlus,
  Inbox,
  Send,
  Pencil,
  Trash,
  User,
  BookOpen,
} from "lucide-react"
import { useReducer } from "react"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import Flash from "@/components/ui/flash"
import { EditProps, useEditProps } from "./page-props"
import { useTranslation } from "react-i18next"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Link, useNavigate } from "@/lib/page-rendering"

const isSheet = new URLSearchParams(window.location.search).get("sheet") === "1"

type ReducerState = {
  items: Array<EditProps["change_wishlist"]["items"][number] & { id: string }>
}

type ReducerAction =
  | { type: "incrementRequest"; payload: { id: string } }
  | { type: "decrementRequest"; payload: { id: string } }
  | { type: "removeRequest"; payload: { id: string } }

function swapItems(
  items: ReducerState["items"],
  currentIndex: number,
  desiredIndex: number,
): ReducerState {
  ;[items[desiredIndex], items[currentIndex]] = [
    items[currentIndex],
    items[desiredIndex],
  ]

  items[currentIndex].rank = desiredIndex
  items[desiredIndex].rank = currentIndex

  return { items }
}

function reducer(state: ReducerState, action: ReducerAction): ReducerState {
  const currentIndex = state.items.findIndex(
    (item) => item.id === action.payload.id,
  )

  switch (action.type) {
    case "incrementRequest": {
      if (currentIndex === 0) {
        return state
      }

      const desiredIndex = currentIndex - 1
      return swapItems([...state.items], currentIndex, desiredIndex)
    }
    case "decrementRequest": {
      if (currentIndex === state.items.length - 1) {
        return state
      }

      const desiredIndex = currentIndex + 1
      return swapItems([...state.items], currentIndex, desiredIndex)
    }
    case "removeRequest": {
      return {
        items: state.items.filter((item) => item.id !== action.payload.id),
      }
    }
    default:
      return state
  }
}

function orderItems(state: ReducerState): ReducerState {
  return {
    items: [...state.items].sort((a, b) => a.rank - b.rank),
  }
}

function transformStateToParams(state: ReducerState) {
  return {
    change_wishlist: {
      items_attributes: state.items.map(
        ({
          joining_instance_index,
          joining_class_name,
          joining_priority_uuid,
        }) => ({
          joining_instance_index,
          joining_priority_uuid,
          details: {
            joining_class_name,
          },
        }),
      ),
    },
  }
}

function transformPropsToState(props: EditProps): ReducerState {
  return {
    items: props.change_wishlist.items.map((item) => ({
      ...item,
      id: `${item.joining_priority_uuid}-${item.joining_instance_index}`,
    })),
  }
}

export default function Page() {
  const navigate = useNavigate()
  const editProps = useEditProps()
  const { change_wishlist } = editProps
  const { t } = useTranslation(["change_wishlists", "common"])

  const [state, dispatch] = useReducer(
    reducer,
    transformPropsToState(editProps),
    orderItems,
  )
  const maxRank = state.items.length - 1
  const disabled = change_wishlist.state !== "open"

  const save = () => {
    navigate({
      href: learnersStudentChangeWishlist({
        studentId: change_wishlist.student_id,
        id: change_wishlist.id,
      }).url,
      method: "patch",
      data: transformStateToParams(state),
    })
  }

  return (
    <Body>
      <Header />
      {/* Inside a sheet the page renders no chrome, so it places the flash. */}
      {isSheet && <Flash />}
      <DetailsCard />
      <PageCard>
        <CardHeader>
          <CardTitle>{t("edit.rankings")}</CardTitle>
        </CardHeader>
        <Container>
          {change_wishlist.state !== "open" && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`edit.status.${change_wishlist.state}`)}
              </AlertDescription>
            </Alert>
          )}

          {state.items.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia
                  variant="icon"
                  className="text-muted size-20 bg-white"
                >
                  <Inbox className="size-16" />
                </EmptyMedia>
                <EmptyTitle>{t("edit.empty_list")}</EmptyTitle>
                <EmptyDescription>
                  {" "}
                  {t("edit.empty_description")}{" "}
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button asChild>
                  <Link href={"/search/classes"}>
                    <CirclePlus />
                    {t("edit.empty_action")}
                  </Link>
                </Button>
              </EmptyContent>
            </Empty>
          ) : (
            <>
              <ul className="contents">
                {state.items.map((item, index) => (
                  <li
                    key={item.id}
                    className="flex flex-row items-center justify-between rounded-md border p-3"
                    data-testid="change-wishlist-item"
                  >
                    <div className="flex flex-col">
                      <span>{item.joining_class_name}</span>
                      <span className="text-sm" data-testid="rank">
                        {t("edit.list.rank", { index: index + 1 })}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          dispatch({
                            type: "incrementRequest",
                            payload: { id: item.id },
                          })
                        }
                        disabled={disabled || index === 0}
                        aria-label={t("edit.list.move_up")}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          dispatch({
                            type: "decrementRequest",
                            payload: { id: item.id },
                          })
                        }
                        disabled={disabled || index === maxRank}
                        aria-label={t("edit.list.move_down")}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive-outline"
                        size="sm"
                        onClick={() =>
                          dispatch({
                            type: "removeRequest",
                            payload: { id: item.id },
                          })
                        }
                        disabled={disabled}
                        aria-label={t("common:actions.remove")}
                      >
                        <Trash className="h-4 w-4" />
                        {t("common:actions.remove")}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Button onClick={save} disabled={disabled}>
            {t("common:actions.save")}
          </Button>
        </Container>
      </PageCard>
    </Body>
  )
}

function Container({ children }: { children: React.ReactNode }) {
  if (isSheet) {
    return <div className="flex flex-col gap-6"> {children} </div>
  }

  return <CardContent className="flex flex-col gap-6">{children}</CardContent>
}

function Body({ children }: { children: React.ReactNode }) {
  if (isSheet) {
    return <SheetPageBody>{children}</SheetPageBody>
  }

  return <PageBody>{children}</PageBody>
}

function PageCard({ children }: { children: React.ReactNode }) {
  if (isSheet) {
    return children
  }
  return <Card>{children}</Card>
}

function Header() {
  const navigate = useNavigate()
  const { t } = useTranslation(["change_wishlists", "common"])
  const { change_wishlist } = useEditProps()

  if (isSheet) {
    return <SheetPageTitle icon={Pencil}>{t("edit.title")}</SheetPageTitle>
  }

  return (
    <LearnerPageChrome
      action={
        change_wishlist.state === "open" && (
          <ConfirmationDialog
            action={
              <Button
                onClick={() =>
                  navigate({
                    href: learnersStudentChangeWishlistPublication({
                      studentId: change_wishlist.student_id,
                      changeWishlistId: change_wishlist.id,
                    }).url,
                    method: "post",
                  })
                }
              >
                {t("common:actions.publish")}
              </Button>
            }
          >
            <ConfirmationDialogTrigger asChild>
              <ActionButton Icon={Send} variant="solid">
                {t("common:actions.publish")}
              </ActionButton>
            </ConfirmationDialogTrigger>
          </ConfirmationDialog>
        )
      }
    />
  )
}

// Cancelling is the wishlist's secondary action, so it sits with the
// details rather than in the page header, which carries Publish alone.
function CancelWishlist() {
  const navigate = useNavigate()
  const { t } = useTranslation(["change_wishlists", "common"])
  const { change_wishlist } = useEditProps()

  return (
    <ConfirmationDialog
      action={
        <Button
          onClick={() =>
            navigate({
              href: learnersStudentChangeWishlistCancellation({
                studentId: change_wishlist.student_id,
                changeWishlistId: change_wishlist.id,
              }).url,
              method: "post",
            })
          }
          variant={"secondary"}
        >
          {t("edit.cancel")}
        </Button>
      }
    >
      <ConfirmationDialogTrigger asChild>
        <ActionButton Icon={Ban}>{t("common:actions.cancel")}</ActionButton>
      </ConfirmationDialogTrigger>
    </ConfirmationDialog>
  )
}

function DetailsCard() {
  const { change_wishlist } = useEditProps()
  const { t } = useTranslation("change_wishlists")

  return (
    <PageCard>
      <CardHeader>
        <CardTitle>{t("edit.details")}</CardTitle>
      </CardHeader>
      <Container>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex w-full flex-row items-center gap-4">
            <BookOpen />
            <div className="flex flex-col">
              <span className="text-muted-foreground">
                {t("edit.leaving_class_name")}
              </span>
              <span>{change_wishlist.leaving_class_name}</span>
            </div>
          </div>
          <div className="flex w-full flex-row items-center gap-4">
            <User />
            <div className="flex w-full flex-col">
              <span className="text-muted-foreground">
                {t("edit.student_name")}
              </span>
              <span>{change_wishlist.student_name}</span>
            </div>
          </div>
        </div>
        {change_wishlist.cancellable && (
          <div className="mt-4 flex justify-end">
            <CancelWishlist />
          </div>
        )}
      </Container>
    </PageCard>
  )
}
